import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { throttle } from 'lodash';
import { useIntl } from 'react-intl';

import { useListMonitoringGateways } from 'src/features/monitoring/api/useListMonitoringGateways';
import GatewayActionModal from 'src/features/monitoring/components/GatewayActionModal';
import MonitoringTabRow from 'src/features/monitoring/components/MonitoringTabRow';
import SingleMonitoringGateway from 'src/features/monitoring/components/SingleMonitoringGateway';
import { GatewaySensorState, GatewayState } from 'src/features/monitoring/types';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { POSITION } from 'src/features/systems/utils/types';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { GatewayActionState, WebSocketEvent, WebSocketEventMessages } from 'src/features/visitor/types';
import { useParking } from 'src/providers/ParkingProvider';
import { SectionBlock, SectionBlockBody } from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';

/** The fetched state of a loop and the WS edge we hold for the same position can be at different
 *  ages in either direction: a re-fetch may be newer than the last event that reached us, and an
 *  event newer than the last fetch. Both carry the backend's own timestamp for the edge, so the
 *  later one wins - which is what keeps the two sources from overwriting each other's fresh value. */
const freshestLoop = (fetched: GatewaySensorState, live?: GatewaySensorState) => {
    if (!live) return fetched;
    if (!fetched.changed_at) return live;
    if (!live.changed_at) return fetched;

    return Date.parse(live.changed_at) >= Date.parse(fetched.changed_at) ? live : fetched;
};

export default function MonitoringGateways() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const [isOpen, setIsOpen] = React.useState(true);
    const [visitorKeyPairs, setVisitorKeyPairs] = React.useState<{
        [key: string]: {
            currentLicensePlate?: string;
            currentNormalizedLicensePlate?: string | null;
            pastVisitorId?: string;
        };
    }>({});
    const [gatewayStates, setGatewayStates] = React.useState<{ [key: string]: GatewayState | null }>({});
    const [gatewayAction, setGatewayAction] = React.useState<GatewayActionState>(null);
    // Busy/free for SENSOR gateways. `gatewayLoopStates` holds the per-loop WS edges, keyed by position;
    // it is an OVERLAY on `gateway.sensors`, not a seeded copy, so a loop no event has arrived for keeps
    // its fetched value. Both the per-loop badges and the gateway's busy/free badge read from the merged
    // result, so the two can never disagree - which is why the event's own `is_busy` aggregate is not
    // kept separately. SENSORLESS gateways instead use `lprBusyKeyPairs` (busy on an LPR detection, free
    // once its pass registers).
    const [gatewayLoopStates, setGatewayLoopStates] = React.useState<{
        [key: string]: { [position: string]: GatewaySensorState };
    }>({});
    const [lprBusyKeyPairs, setLprBusyKeyPairs] = React.useState<{ [key: string]: boolean }>({});

    const { data, isLoading } = useListMonitoringGateways({ parkingId });
    const { subscribe, unsubscribe } = React.useContext(WSContext);

    // Seed the WS-driven state for gateways the first time we see them in the API response,
    // so the UI shows the persisted current/last visitor and opened/closed state on first
    // load instead of waiting for a WS event. Subsequent re-fetches don't overwrite live
    // updates because we only seed keys that aren't already in local state.
    React.useEffect(() => {
        if (!data) return;
        setVisitorKeyPairs((prev) => {
            const next = { ...prev };
            data.results.forEach((gateway) => {
                if (next[gateway.id]) return;
                next[gateway.id] = {
                    currentLicensePlate: gateway.current_visitor?.license_plate,
                    currentNormalizedLicensePlate: gateway.current_visitor?.normalized_license_plate,
                    pastVisitorId: gateway.last_visitor_today?.id,
                };
            });
            return next;
        });
        setGatewayStates((prev) => {
            const next = { ...prev };
            data.results.forEach((gateway) => {
                if (gateway.id in next) return;
                next[gateway.id] = gateway.state;
            });
            return next;
        });
        setLprBusyKeyPairs((prev) => {
            const next = { ...prev };
            data.results.forEach((gateway) => {
                if (gateway.id in next) return;
                next[gateway.id] = gateway.is_busy;
            });
            return next;
        });
    }, [data]);

    const applyEvent = React.useCallback((event: WebSocketEvent) => {
        if (
            event.message === WebSocketEventMessages.NEW_ENTRY_PASS ||
            event.message === WebSocketEventMessages.NEW_EXIT_PASS
        ) {
            setVisitorKeyPairs((prevState) => ({
                ...prevState,
                [event.data.gateway_id]: {
                    pastVisitorId: event.data.visitor_id,
                },
            }));
            // Sensorless gateways read busy from here; the registered pass means the vehicle left -> free.
            setLprBusyKeyPairs((prevState) => ({ ...prevState, [event.data.gateway_id]: false }));
        } else if (event.message === WebSocketEventMessages.NEW_LPR_DETECTION) {
            setVisitorKeyPairs((prevState) => ({
                ...prevState,
                [event.data.gateway_id]: {
                    ...prevState[event.data.gateway_id],
                    currentLicensePlate: event.data.license_plate,
                    currentNormalizedLicensePlate: event.data.normalized_license_plate,
                },
            }));
            // Sensorless gateways read busy from here; a detection waiting for its pass -> busy.
            setLprBusyKeyPairs((prevState) => ({ ...prevState, [event.data.gateway_id]: true }));
        } else if (event.message === WebSocketEventMessages.GATEWAY_STATE_CHANGED) {
            setGatewayStates((prevState) => ({
                ...prevState,
                [event.data.gateway_id]: event.data.state,
            }));
        } else if (event.message === WebSocketEventMessages.GATEWAY_SENSOR_STATE_CHANGED) {
            const { gateway_id, sensor } = event.data;
            // The event's `is_busy` is the backend's aggregate at edge time; we re-derive it from the
            // merged per-loop states instead so the busy badge and the loop badges always agree.
            if (sensor) {
                setGatewayLoopStates((prevState) => ({
                    ...prevState,
                    [gateway_id]: {
                        ...(prevState[gateway_id] ?? {}),
                        [sensor.position]: {
                            id: sensor.id,
                            // The backend drops edges whose position is not one of the enum values
                            // before they ever reach the WS layer, so the payload can only carry one.
                            position: sensor.position as POSITION,
                            activated: sensor.activated,
                            changed_at: sensor.changed_at,
                        },
                    },
                }));
            }
        }
    }, []);

    // The throttle bounds re-renders, but it must not decide WHICH events survive: throttling the
    // handler itself dropped every event but the first and last of a window, and two loops toggling
    // within half a second is the normal case - a dropped edge leaves a badge stuck. So every event is
    // buffered as it arrives and the throttle only paces the flush. Buffer, throttle and subscription
    // all live in this effect so they are created and torn down together.
    React.useEffect(() => {
        const pending: WebSocketEvent[] = [];
        const flush = throttle(() => pending.splice(0, pending.length).forEach(applyEvent), 500);
        const handleWebSocket = (event: WebSocketEvent) => {
            pending.push(event);
            flush();
        };

        subscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);

        return () => {
            unsubscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);
            flush.cancel();
        };
    }, [applyEvent, subscribe, unsubscribe]);

    return (
        <>
            <GatewayActionModal gatewayAction={gatewayAction} setGatewayAction={setGatewayAction} />
            <div className="flex items-center justify-between">
                <MonitoringTabRow />
                <div className="flex justify-end">
                    <Button size="mdIcon" variant="outline" onClick={() => setIsOpen((prevState) => !prevState)}>
                        <FontAwesomeIcon icon={isOpen ? ICON_MAP.COMPRESS : ICON_MAP.EXPAND} />
                        <span className="hidden md:block">
                            {formatMessage(
                                isOpen ? parkingStatusMessages.collapseAll : parkingStatusMessages.expandAll,
                            )}
                        </span>
                    </Button>
                </div>
            </div>
            <SectionBlock>
                <SectionBlockBody className="grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {isLoading &&
                        Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton key={index} className="h-160 rounded-xl" />
                        ))}
                    {data?.results.map((gateway) => {
                        // The fetched loops are the base and the WS edges the overlay, so the order
                        // stays the backend's (front -> center -> back) and a loop no event has
                        // arrived for keeps its fetched value instead of disappearing. Busy is the
                        // aggregate over the same merged list, never a separately tracked flag.
                        const liveLoops = gatewayLoopStates[gateway.id];
                        const loops = gateway.sensors
                            ? gateway.sensors.map((sensor) => freshestLoop(sensor, liveLoops?.[sensor.position]))
                            : [];
                        const isBusy =
                            gateway.has_sensors || false
                                ? loops.some((loop) => loop.activated === true)
                                : (lprBusyKeyPairs[gateway.id] ?? gateway.is_busy);

                        return (
                            <SingleMonitoringGateway
                                key={gateway.id}
                                gateway={gateway}
                                isOpen={isOpen}
                                isBusy={isBusy}
                                loops={loops}
                                visitors={visitorKeyPairs[gateway.id]}
                                currentState={gatewayStates[gateway.id] ?? gateway.state}
                                setGatewayAction={setGatewayAction}
                            />
                        );
                    })}
                </SectionBlockBody>
            </SectionBlock>
        </>
    );
}
